import { useState, useMemo, useRef, useEffect } from "react";
import { IWorkspaceMember } from "dto/IWorkspace";
import { PrivilegesData, loadPrivileges, savePrivileges } from "./privilegesUtils";
import styles from "./styles.module.scss";

const INTEGRATION_SECTIONS = [
  { key: "email", label: "Email / IMAP" },
  { key: "ksef", label: "KSeF — Krajowy System e-Faktur" },
  { key: "court", label: "Portal Informacyjny sądów" },
  { key: "bank", label: "Bankowość" },
  { key: "office365", label: "Microsoft 365 / Outlook" },
  { key: "gsuite", label: "Google Workspace" },
];

// ── Owner section ─────────────────────────────────────────────────

function OwnerSection({ owner }: { owner: IWorkspaceMember | undefined }) {
  const name = owner?.Label ?? owner?.Email ?? "—";
  const initial = name[0]?.toUpperCase() ?? "?";

  return (
    <div className={styles.section}>
      <div className={styles.ownerHeader}>
        <div className={styles.ownerLeft}>
          <span className={styles.ownerIcon}>★</span>
          <span className={styles.sectionTitle}>Właściciel konta</span>
        </div>
        <div className={styles.ownerIdentity}>
          <span className={styles.ownerName}>{name}</span>
          <div className={styles.memberAvatar} title={name}>{initial}</div>
        </div>
      </div>
    </div>
  );
}

// ── People picker panel ───────────────────────────────────────────

function PeoplePicker({
  available,
  onSelect,
  onClose,
}: {
  available: IWorkspaceMember[];
  onSelect: (userId: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return available.filter(
      (m) =>
        (m.Label ?? "").toLowerCase().includes(q) ||
        (m.Email ?? "").toLowerCase().includes(q),
    );
  }, [available, query]);

  return (
    <div className={styles.pickerPanel} ref={panelRef}>
      <input
        ref={inputRef}
        className={styles.pickerSearch}
        placeholder="Szukaj osoby…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
      />
      <div className={styles.pickerList}>
        {filtered.length === 0 ? (
          <div className={styles.pickerEmpty}>Brak dostępnych osób</div>
        ) : (
          filtered.map((m) => {
            const label = m.Label ?? m.Email ?? m.UserId;
            return (
              <button
                key={m.UserId}
                className={styles.pickerOption}
                onClick={() => { onSelect(m.UserId); onClose(); }}
              >
                <div className={styles.memberAvatar}>{label[0]?.toUpperCase()}</div>
                <div className={styles.pickerOptionInfo}>
                  <span className={styles.memberName}>{label}</span>
                  {m.Email && m.Label && (
                    <span className={styles.memberEmail}>{m.Email}</span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Generic section ───────────────────────────────────────────────

interface SectionProps {
  title: string;
  memberIds: string[];
  allMembers: IWorkspaceMember[];
  readOnly?: boolean;
  onAdd?: (userId: string) => void;
  onRemove?: (userId: string) => void;
}

function Section({ title, memberIds, allMembers, readOnly, onAdd, onRemove }: SectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const sectionMembers = useMemo(
    () => allMembers.filter((m) => memberIds.includes(m.UserId)),
    [allMembers, memberIds],
  );

  const available = useMemo(
    () => allMembers.filter((m) => !memberIds.includes(m.UserId)),
    [allMembers, memberIds],
  );

  const handleAdd = (userId: string) => {
    onAdd?.(userId);
    setPickerOpen(false);
  };

  return (
    <div className={styles.section}>
      <div className={`${styles.sectionHeader} ${!isOpen ? styles.sectionHeaderCollapsed : ""}`}>
        <button
          className={styles.collapseBtn}
          onClick={() => { setIsOpen((v) => !v); setPickerOpen(false); }}
          title={isOpen ? "Zwiń" : "Rozwiń"}
        >
          {isOpen ? "▼" : "▶"}
        </button>

        <span className={styles.sectionTitle}>{title}</span>

        <span className={styles.memberCount} title="Liczba przypisanych osób">
          {memberIds.length}
        </span>

        {!readOnly && isOpen && (
          <button
            className={`${styles.addMemberBtn} ${pickerOpen ? styles.addMemberBtnActive : ""}`}
            onClick={() => setPickerOpen((v) => !v)}
          >
            + Dodaj osobę
          </button>
        )}
      </div>

      {isOpen && (
        <>
          {pickerOpen && !readOnly && (
            <PeoplePicker
              available={available}
              onSelect={handleAdd}
              onClose={() => setPickerOpen(false)}
            />
          )}

          <div className={styles.memberList}>
            {sectionMembers.length === 0 ? (
              <div className={styles.emptyHint}>Brak przypisanych osób.</div>
            ) : (
              sectionMembers.map((m) => {
                const label = m.Label ?? m.Email ?? m.UserId;
                return (
                  <div key={m.UserId} className={styles.memberRow}>
                    <div className={styles.memberAvatar}>{label[0]?.toUpperCase()}</div>
                    <div className={styles.memberInfo}>
                      <span className={styles.memberName}>{label}</span>
                      {m.Email && m.Label && (
                        <span className={styles.memberEmail}>{m.Email}</span>
                      )}
                    </div>
                    {!readOnly && (
                      <button
                        className={styles.removeBtn}
                        onClick={() => onRemove?.(m.UserId)}
                        title="Usuń z sekcji"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────

interface Props {
  members: IWorkspaceMember[];
  me: IWorkspaceMember | undefined;
}

const PrivilegesTab = ({ members, me }: Props) => {
  const [data, setData] = useState<PrivilegesData>(() => loadPrivileges());

  const persist = (next: PrivilegesData) => { setData(next); savePrivileges(next); };

  const ownerMember = members.find((m) => m.IsOwner);

  const isOwner = !!me?.IsOwner;
  const isPartner = data.partners.includes(me?.UserId ?? "");
  const isAdmin = data.admins.includes(me?.UserId ?? "");

  const canEditPartners = isOwner;
  const canEditAdmins = isOwner || isPartner;
  const canEditIntegrations = isOwner || isPartner || isAdmin;

  const addTo = (key: "partners" | "admins") => (userId: string) =>
    persist({ ...data, [key]: [...data[key], userId] });

  const removeFrom = (key: "partners" | "admins") => (userId: string) =>
    persist({ ...data, [key]: data[key].filter((id) => id !== userId) });

  const addToIntegration = (intKey: string) => (userId: string) => {
    const current = data.integrations[intKey] ?? [];
    persist({ ...data, integrations: { ...data.integrations, [intKey]: [...current, userId] } });
  };

  const removeFromIntegration = (intKey: string) => (userId: string) => {
    const current = data.integrations[intKey] ?? [];
    persist({
      ...data,
      integrations: { ...data.integrations, [intKey]: current.filter((id) => id !== userId) },
    });
  };

  return (
    <div className={styles.root}>
<div className={styles.systemSections}>
        <div className={styles.groupLabel}>Sekcje systemowe</div>

        <OwnerSection owner={ownerMember} />

        <Section
          title="Wspólnicy / Partnerzy"
          memberIds={data.partners}
          allMembers={members}
          readOnly={!canEditPartners}
          onAdd={addTo("partners")}
          onRemove={removeFrom("partners")}
        />

        <Section
          title="Administratorzy"
          memberIds={data.admins}
          allMembers={members}
          readOnly={!canEditAdmins}
          onAdd={addTo("admins")}
          onRemove={removeFrom("admins")}
        />
      </div>

      <div className={styles.integrationSections}>
        <div className={styles.groupLabel}>Integracje</div>
        {INTEGRATION_SECTIONS.map((integ) => (
          <Section
            key={integ.key}
            title={integ.label}
            memberIds={data.integrations[integ.key] ?? []}
            allMembers={members}
            readOnly={!canEditIntegrations}
            onAdd={addToIntegration(integ.key)}
            onRemove={removeFromIntegration(integ.key)}
          />
        ))}
      </div>
    </div>
  );
};

export default PrivilegesTab;
